/**
 * ProtocolLibraryPage — restricted, de-identified protocol vault
 * (Daniel/Dr. Luis, Jun 12 2026).
 *
 * Content is TOTALLY RESTRICTED: a user sees nothing until granted explicit
 * access (regardless of role). Sysadmin / Chief Medical Director manage grants.
 * Protocols start with an IV base tailored to the condition, then layer the
 * modality toolkit (MSC cord/dental/fat, MUSE, NK, exosomes, PRP, peptide).
 * No PHI/PII — these are de-identified protocols (IP).
 */
import React, { useState, useEffect, useCallback } from "react";
import { Lock, Loader2, FlaskConical, Plus, ShieldCheck, AlertTriangle, RefreshCcw, X, Upload, FileText, Download, Archive, Search, Check, Sparkles, Pencil } from "lucide-react";

const CATEGORIES = [["common", "Common"], ["chronic", "Chronic disease"], ["terminal", "Serious / advanced"]];
const MODALITIES = [
  ["msc_cord", "MSC (cord)"], ["msc_dental", "MSC (dental)"], ["msc_fat", "MSC (fat)"],
  ["muse", "MUSE"], ["nk", "NK cells"], ["exosome", "Exosomes"], ["prp", "PRP"], ["peptide", "Peptide maintenance"],
];
const MOD_LABEL = Object.fromEntries(MODALITIES);
function hasPerm(user, p) { return new Set((user && user.permissions) || []).has(p); }

export default function ProtocolLibraryPage({ user }) {
  const canView = hasPerm(user, "protocol_vault:view");
  const canGenerate = hasPerm(user, "protocol_vault:generate");
  const [access, setAccess] = useState(null); // { granted, isAdmin }
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [showGrants, setShowGrants] = useState(false);
  const [encCheck, setEncCheck] = useState(null); // null | "loading" | result | {error}
  const [search, setSearch] = useState("");   // input box
  const [query, setQuery] = useState("");      // applied search term
  const [drafts, setDrafts] = useState([]);    // AI-extracted, pending review
  const [editDraft, setEditDraft] = useState(null); // draft being edited

  async function archive(p) {
    if (!window.confirm(`Archive “${p.protocol_name}”? It will be hidden from the library. The vault keeps an audited record (no hard delete).`)) return;
    const reason = window.prompt("Optional note (e.g. “test entry”):", "") || "";
    try {
      const r = await window.zignagenixAPI.invoke("protocols:retire", { id: p.id, reason });
      if (r && r.error) setError(r.message || r.code); else await load();
    } catch (e) { setError((e && e.message) || "Archive failed"); }
  }

  async function runEncCheck() {
    setEncCheck("loading");
    try {
      const r = await window.zignagenixAPI.invoke("protocols:encryption-selfcheck");
      setEncCheck(r || { error: true, message: "No response" });
    } catch (e) { setEncCheck({ error: true, message: (e && e.message) || "Failed" }); }
  }

  const load = useCallback(async (q) => {
    if (!canView) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const a = await window.zignagenixAPI.invoke("protocols:access-status");
      setAccess(a || { granted: false, isAdmin: false });
      if (a && a.granted) {
        const list = await window.zignagenixAPI.invoke("protocols:list", { limit: 300, q: q || "" });
        setProtocols(Array.isArray(list) ? list : []);
        try {
          const d = await window.zignagenixAPI.invoke("protocols:drafts:list");
          setDrafts(Array.isArray(d) ? d : []);
        } catch (_) { setDrafts([]); }
      }
    } catch (e) { setError((e && e.message) || "Failed to load"); }
    setLoading(false);
  }, [canView]);

  useEffect(() => { load(""); }, [load]);

  function runSearch() { const q = search.trim(); setQuery(q); load(q); }

  async function reviewDraft(id, action, reason) {
    try {
      const ch = action === "approve" ? "protocols:drafts:approve" : "protocols:drafts:reject";
      const r = await window.zignagenixAPI.invoke(ch, { id, reason });
      if (r && r.error) setError(r.message || r.code); else load(query);
    } catch (e) { setError((e && e.message) || "Action failed"); }
  }

  if (!canView) return <div style={{ padding: 24, color: "#5a6478" }}>You don't have access to the Protocol Library.</div>;
  if (loading) return <div style={{ padding: 24, color: "#9aa4b2" }}><Loader2 size={14} className="spin" /> Loading…</div>;

  return (
    <div style={{ padding: "16px 24px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <FlaskConical size={22} color="#2E8B98" />
          <div>
            <h1 style={{ fontSize: 21, margin: 0 }}>Protocol Library</h1>
            <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>Restricted, de-identified protocols (no PHI). IV-first, tailored to condition.</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {access && access.isAdmin && <button onClick={() => setShowGrants(true)} style={btnSecondary}><ShieldCheck size={13} /> Manage access</button>}
          {access && access.isAdmin && <button onClick={runEncCheck} style={btnSecondary}><Lock size={13} /> Verify encryption</button>}
          {canGenerate && <button onClick={() => setShowGen(true)} style={btnPrimary}><Sparkles size={13} /> Generate with AI</button>}
          {access && access.granted && <button onClick={() => setShowNew(true)} style={btnPrimary}><Plus size={13} /> New protocol</button>}
          <button onClick={load} style={btnSecondary}><RefreshCcw size={13} /></button>
        </div>
      </div>

      {error && <div style={errBox}><AlertTriangle size={14} /> {error}</div>}

      {encCheck && <EncCheckPanel data={encCheck} onClose={() => setEncCheck(null)} />}

      {access && !access.granted ? (
        <div style={{ border: "1px solid #e1e6ed", borderRadius: 8, background: "#fff", padding: 28, textAlign: "center" }}>
          <Lock size={28} color="#9aa4b2" />
          <h2 style={{ fontSize: 16, margin: "10px 0 4px" }}>Access restricted</h2>
          <p style={{ color: "#5a6478", fontSize: 13, maxWidth: 460, margin: "0 auto" }}>
            The protocol library is restricted. Viewing protocols requires an explicit access grant —
            independent of your role. Request access from a sysadmin or the Chief Medical Director.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Search — runs server-side AFTER decryption (no plaintext index). */}
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search size={14} color="#9aa4b2" style={{ position: "absolute", left: 9, top: 9 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                placeholder="Search protocols by name, indication, IV base, summary…"
                style={{ ...inp, paddingLeft: 28 }} />
            </div>
            <button onClick={runSearch} style={btnSecondary}><Search size={13} /> Search</button>
            {query && <button onClick={() => { setSearch(""); setQuery(""); load(""); }} style={btnTinyGhost}>Clear</button>}
          </div>
          {query && <div style={{ fontSize: 11.5, color: "#6C757D" }}>Showing matches for “{query}” — {protocols.length} result(s).</div>}

          {/* AI extraction review queue — drafts split from uploaded documents. */}
          {drafts.length > 0 && (
            <div style={{ border: "1px solid #f3d9a6", borderRadius: 8, background: "#fffbf2", padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Sparkles size={16} color="#b8860b" />
                <strong style={{ fontSize: 13.5 }}>AI-extracted protocols — review queue</strong>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#b8860b", background: "#fbe6c2", padding: "1px 8px", borderRadius: 10 }}>{drafts.length} pending</span>
              </div>
              <div style={{ fontSize: 11.5, color: "#8a6d3b", marginBottom: 10 }}>
                Split from uploaded documents. Review each, edit if needed, then approve to publish (or reject). Nothing here is in the live library yet.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {drafts.map((d) => (
                  <div key={d.id} style={{ border: "1px solid #eadfc4", borderRadius: 6, background: "#fff", padding: 11 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <strong style={{ fontSize: 13.5, color: "#1F2937" }}>{d.protocol_name}</strong>
                      {d.condition_category && <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#2E8B98", background: "#e7f3f5", padding: "1px 6px", borderRadius: 10 }}>{(CATEGORIES.find((c) => c[0] === d.condition_category) || [])[1] || d.condition_category}</span>}
                      {d.source_document_id && <span style={{ fontSize: 10.5, color: "#9aa4b2" }}>from doc #{d.source_document_id}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: "#3a4a5e", marginTop: 2 }}>Indication: {d.target_indication}</div>
                    {d.iv_base && <div style={{ fontSize: 11.5, color: d.iv_base.startsWith("(IV base not specified") ? "#b45309" : "#6C757D", marginTop: 2 }}><b>IV base:</b> {d.iv_base}</div>}
                    {Array.isArray(d.modalities) && d.modalities.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                        {d.modalities.map((m) => <span key={m} style={{ fontSize: 10.5, color: "#5a6478", background: "#f4f7fb", border: "1px solid #e1e6ed", padding: "1px 7px", borderRadius: 10 }}>{MOD_LABEL[m] || m}</span>)}
                      </div>
                    )}
                    {d.protocol_summary && <div style={{ fontSize: 11.5, color: "#6C757D", marginTop: 5 }}>{d.protocol_summary}</div>}
                    {access && access.isAdmin && (
                      <div style={{ display: "flex", gap: 6, marginTop: 9 }}>
                        <button onClick={() => setEditDraft(d)} style={btnSm}><Pencil size={12} /> Edit</button>
                        <button onClick={() => reviewDraft(d.id, "approve")} style={{ ...btnSm, background: "#e6f7ee", color: "#1a7f5a", borderColor: "#b6e3cd" }}><Check size={12} /> Approve</button>
                        <button onClick={() => { const r = window.prompt("Reject — optional reason:", ""); if (r !== null) reviewDraft(d.id, "reject", r); }} style={btnTinyGhost}><X size={12} /> Reject</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top-level document upload — always available to granted users,
              even before any structured protocol exists (unfiled docs). */}
          <div style={{ border: "1px solid #e1e6ed", borderRadius: 6, background: "#fff", padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FileText size={15} color="#2E8B98" />
              <strong style={{ fontSize: 13.5 }}>Protocol Documents</strong>
              <span style={{ fontSize: 11.5, color: "#9aa4b2" }}>upload protocol files (PDF, Word, Excel…); restricted + audited</span>
            </div>
            <ProtocolDocs protocolId={null} onExtracted={() => load(query)} />
          </div>

          {protocols.length === 0 ? (
            <div style={{ fontSize: 13, color: "#9aa4b2", fontStyle: "italic", padding: 12 }}>No structured protocols yet. Use “New protocol” to add one, or upload documents above.</div>
          ) : protocols.map((p) => (
            <div key={p.id} style={{ border: "1px solid #e1e6ed", borderRadius: 6, background: "#fff", padding: 14 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ fontSize: 14, color: "#1F2937" }}>{p.protocol_name}</strong>
                <span style={{ fontSize: 11, color: "#9aa4b2" }}>{p.protocol_version}</span>
                {p.condition_category && <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", color: "#2E8B98", background: "#e7f3f5", padding: "1px 7px", borderRadius: 10 }}>{(CATEGORIES.find((c) => c[0] === p.condition_category) || [])[1] || p.condition_category}</span>}
                {access && access.isAdmin && <button onClick={() => archive(p)} style={{ ...btnTinyGhost, marginLeft: "auto" }}><Archive size={11} /> Archive</button>}
              </div>
              <div style={{ fontSize: 12.5, color: "#3a4a5e", marginTop: 3 }}>Indication: {p.target_indication}</div>
              {p.iv_base && <div style={{ fontSize: 12, color: "#6C757D", marginTop: 3 }}><b>IV base:</b> {p.iv_base}</div>}
              {Array.isArray(p.modalities) && p.modalities.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
                  {p.modalities.map((m) => <span key={m} style={{ fontSize: 11, color: "#5a6478", background: "#f4f7fb", border: "1px solid #e1e6ed", padding: "2px 8px", borderRadius: 10 }}>{MOD_LABEL[m] || m}</span>)}
                </div>
              )}
              {p.maintenance_program && <div style={{ fontSize: 12, color: "#6C757D", marginTop: 6 }}><b>Maintenance:</b> {p.maintenance_program}</div>}
              {p.protocol_summary && <div style={{ fontSize: 12, color: "#6C757D", marginTop: 6 }}>{p.protocol_summary}</div>}
              <ProtocolDocs protocolId={p.id} onExtracted={() => load(query)} />
            </div>
          ))}
        </div>
      )}

      {showNew && <NewProtocolModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(query); }} />}
      {editDraft && <NewProtocolModal initial={editDraft} onClose={() => setEditDraft(null)} onSaved={() => { setEditDraft(null); load(query); }} />}
      {showGen && <GenerateModal onClose={() => setShowGen(false)} onDone={() => load(query)} />}
      {showGrants && <GrantsModal onClose={() => setShowGrants(false)} />}
    </div>
  );
}

function EncCheckPanel({ data, onClose }) {
  if (data === "loading") return <div style={{ ...encBox, color: "#5a6478" }}><Loader2 size={13} className="spin" /> Reading raw stored bytes…</div>;
  if (data.error) return <div style={errBox}><AlertTriangle size={14} /> {data.message || data.code || "Check failed"} <button onClick={onClose} style={{ ...btnTinyGhost, marginLeft: "auto", color: "#5a6478", borderColor: "#c9d6e8" }}>Dismiss</button></div>;
  if (data.code === "NO_PROTOCOLS") return <div style={{ ...encBox, color: "#5a6478" }}><AlertTriangle size={14} color="#b8860b" /> {data.message} <button onClick={onClose} style={{ ...btnTinyGhost, marginLeft: "auto", color: "#5a6478", borderColor: "#c9d6e8" }}>Dismiss</button></div>;

  const fields = data.encrypted_at_rest || {};
  const labels = { protocol_name: "Protocol name", target_indication: "Indication", iv_base: "IV base", dosage_regimen: "Dosage regimen" };
  const statusOf = (f) => f.status || (f.encrypted ? "encrypted" : "plaintext");
  const leak = Object.values(fields).some((f) => statusOf(f) === "plaintext");
  const COLOR = { encrypted: "#1a7f5a", empty: "#9aa4b2", plaintext: "#8b0000" };
  const TEXT = { encrypted: "encrypted", empty: "empty", plaintext: "PLAINTEXT" };
  return (
    <div style={{ ...encBox, flexDirection: "column", alignItems: "stretch", ...(leak ? { background: "#fde8e8", border: "1px solid #f5a5a5" } : {}) }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {leak ? <AlertTriangle size={16} color="#8b0000" /> : <ShieldCheck size={16} color="#1a7f5a" />}
        <strong style={{ fontSize: 13 }}>{leak ? "⚠ Plaintext detected" : "Encrypted at rest ✓"}</strong>
        <span style={{ fontSize: 11, color: "#9aa4b2" }}>raw DB bytes of protocol #{data.protocol_id} (decryption bypassed)</span>
        <button onClick={onClose} style={{ ...btnTinyGhost, marginLeft: "auto", color: "#5a6478", borderColor: "#c9d6e8" }}>Dismiss</button>
      </div>
      {Object.entries(fields).map(([k, f]) => { const s = statusOf(f); return (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 0" }}>
          <span style={{ width: 130, color: "#3a4a5e", fontWeight: 600 }}>{labels[k] || k}</span>
          <span style={{ width: 78, color: COLOR[s], fontWeight: 700 }}>{TEXT[s]}</span>
          <code style={{ fontSize: 11, color: "#6C757D", background: "#f4f7fb", padding: "1px 6px", borderRadius: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.preview}</code>
        </div>
      ); })}
      <div style={{ fontSize: 11, color: "#9aa4b2", marginTop: 4 }}>“empty” = field left blank on this protocol (no data to encrypt) — not a leak.</div>
      <div style={{ fontSize: 11, color: "#9aa4b2", marginTop: 6 }}>
        <code style={{ fontSize: 11 }}>condition_category</code> = “{(data.plaintext_index_by_design || {}).condition_category}” is plaintext by design (non-IP filter index).
      </div>
    </div>
  );
}

function NewProtocolModal({ initial, onClose, onSaved }) {
  const isEdit = !!(initial && initial.id);
  const [f, setF] = useState({
    protocol_name: initial?.protocol_name || "",
    target_indication: initial?.target_indication || "",
    condition_category: initial?.condition_category || "common",
    iv_base: (initial?.iv_base && !String(initial.iv_base).startsWith("(IV base not specified")) ? initial.iv_base : "",
    maintenance_program: initial?.maintenance_program || "",
    protocol_summary: initial?.protocol_summary || "",
    dosage_regimen: initial?.dosage_regimen || "",
    contraindications: initial?.contraindications || "",
    expected_outcomes: initial?.expected_outcomes || "",
    evidence_basis: initial?.evidence_basis || "",
    modalities: new Set(Array.isArray(initial?.modalities) ? initial.modalities : []),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleMod = (m) => setF((s) => { const x = new Set(s.modalities); x.has(m) ? x.delete(m) : x.add(m); return { ...s, modalities: x }; });

  async function save() {
    setBusy(true); setErr(null);
    try {
      const payload = { ...f, modalities: Array.from(f.modalities) };
      const r = isEdit
        ? await window.zignagenixAPI.invoke("protocols:update", { id: initial.id, ...payload })
        : await window.zignagenixAPI.invoke("protocols:create", payload);
      if (r && r.error) setErr(r.message || r.code); else onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <Modal title={isEdit ? "Edit protocol" : "New protocol"} onClose={onClose}>
      {err && <div style={errBox}>{err}</div>}
      <label style={lbl}>Protocol name</label><input style={inp} value={f.protocol_name} onChange={set("protocol_name")} />
      <label style={lbl}>Target condition / indication</label><input style={inp} value={f.target_indication} onChange={set("target_indication")} />
      <label style={lbl}>Category</label>
      <select style={inp} value={f.condition_category} onChange={set("condition_category")}>{CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <label style={lbl}>IV base (tailored to the condition — protocols start here)</label>
      <textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.iv_base} onChange={set("iv_base")} />
      <label style={lbl}>Modality toolkit</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {MODALITIES.map(([m, l]) => (
          <button key={m} type="button" onClick={() => toggleMod(m)} style={{ ...chip, ...(f.modalities.has(m) ? chipOn : {}) }}>{l}</button>
        ))}
      </div>
      <label style={lbl}>Peptide maintenance program</label><textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.maintenance_program} onChange={set("maintenance_program")} />
      <label style={lbl}>Dosage regimen</label><textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.dosage_regimen} onChange={set("dosage_regimen")} />
      <label style={lbl}>Contraindications</label><textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.contraindications} onChange={set("contraindications")} />
      <label style={lbl}>Expected outcomes</label><textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.expected_outcomes} onChange={set("expected_outcomes")} />
      <label style={lbl}>Evidence basis</label><textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.evidence_basis} onChange={set("evidence_basis")} />
      <label style={lbl}>Summary</label><textarea rows={2} style={{ ...inp, resize: "vertical" }} value={f.protocol_summary} onChange={set("protocol_summary")} />
      <button onClick={save} disabled={busy} style={{ ...btnPrimary, marginTop: 10 }}>{busy ? <><Loader2 size={12} className="spin" /> Saving…</> : (isEdit ? "Save changes" : "Save protocol")}</button>
    </Modal>
  );
}

function GenerateModal({ onClose, onDone }) {
  const [f, setF] = useState({ condition: "", condition_category: "common", goals: "", patient_profile: "", modalities: new Set() });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleMod = (m) => setF((s) => { const x = new Set(s.modalities); x.has(m) ? x.delete(m) : x.add(m); return { ...s, modalities: x }; });

  async function generate() {
    if (!f.condition.trim()) { setErr("Enter a target condition."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await window.zignagenixAPI.invoke("protocols:generate", {
        condition: f.condition, condition_category: f.condition_category, goals: f.goals,
        patient_profile: f.patient_profile, preferred_modalities: Array.from(f.modalities),
      });
      if (r && r.error) setErr(r.message || r.code);
      else { setResult(r); onDone && onDone(); }
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const P = result && result.protocol;
  return (
    <Modal title="Generate protocol with AI" onClose={onClose}>
      {err && <div style={errBox}>{err}</div>}
      {!result ? (
        <>
          <div style={{ fontSize: 11.5, color: "#6C757D", marginBottom: 8 }}>
            Describe the condition and goals — the AI (AWS Bedrock) drafts a de-identified, IV-first protocol with the
            modality toolkit. <b>Do not enter patient identifiers</b>; use a profile bucket. The result is saved as a
            draft in the review queue for approval — it is not published automatically and is not medical advice.
          </div>
          <label style={lbl}>Target condition / indication</label>
          <input style={inp} value={f.condition} onChange={set("condition")} placeholder="e.g. Knee osteoarthritis" />
          <label style={lbl}>Category</label>
          <select style={inp} value={f.condition_category} onChange={set("condition_category")}>{CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <label style={lbl}>Patient profile bucket (optional, de-identified)</label>
          <input style={inp} value={f.patient_profile} onChange={set("patient_profile")} placeholder="e.g. adult, BMI 25–35, not on anticoagulants" />
          <label style={lbl}>Clinical goals / context</label>
          <textarea rows={4} style={{ ...inp, resize: "vertical" }} value={f.goals} onChange={set("goals")} placeholder="Goals, prior treatments, constraints, contraindications to avoid…" />
          <label style={lbl}>Preferred modalities (optional)</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {MODALITIES.map(([m, l]) => <button key={m} type="button" onClick={() => toggleMod(m)} style={{ ...chip, ...(f.modalities.has(m) ? chipOn : {}) }}>{l}</button>)}
          </div>
          <button onClick={generate} disabled={busy} style={{ ...btnPrimary, marginTop: 10 }}>{busy ? <><Loader2 size={12} className="spin" /> Generating…</> : <><Sparkles size={13} /> Generate draft</>}</button>
        </>
      ) : (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#e6f7ee", border: "1px solid #b6e3cd", borderRadius: 6, padding: 10, marginBottom: 10 }}>
            <Check size={15} color="#1a7f5a" /><span style={{ fontSize: 12.5, color: "#1a7f5a" }}>Draft generated and saved to the review queue (pending approval).</span>
          </div>
          <strong style={{ fontSize: 14 }}>{P.protocol_name}</strong>
          <div style={{ fontSize: 12.5, color: "#3a4a5e", marginTop: 3 }}>Indication: {P.target_indication}</div>
          {P.iv_base && <div style={{ fontSize: 12, color: "#6C757D", marginTop: 3 }}><b>IV base:</b> {P.iv_base}</div>}
          {Array.isArray(P.modalities) && P.modalities.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
              {P.modalities.map((m) => <span key={m} style={{ fontSize: 11, color: "#5a6478", background: "#f4f7fb", border: "1px solid #e1e6ed", padding: "2px 8px", borderRadius: 10 }}>{MOD_LABEL[m] || m}</span>)}
            </div>
          )}
          {P.maintenance_program && <div style={{ fontSize: 12, color: "#6C757D", marginTop: 6 }}><b>Maintenance:</b> {P.maintenance_program}</div>}
          {P.dosage_regimen && <div style={{ fontSize: 12, color: "#6C757D", marginTop: 6 }}><b>Dosage:</b> {P.dosage_regimen}</div>}
          {P.contraindications && <div style={{ fontSize: 12, color: "#8b0000", marginTop: 6 }}><b>Contraindications:</b> {P.contraindications}</div>}
          {P.protocol_summary && <div style={{ fontSize: 12, color: "#6C757D", marginTop: 6 }}>{P.protocol_summary}</div>}
          <div style={{ fontSize: 11, color: "#9aa4b2", marginTop: 10 }}>AI-generated draft — review and approve in the queue before any use. Not medical advice.</div>
          <button onClick={onClose} style={{ ...btnSecondary, marginTop: 10 }}>Close</button>
        </div>
      )}
    </Modal>
  );
}

function GrantsModal({ onClose }) {
  const [grants, setGrants] = useState([]);
  const [userId, setUserId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try { const r = await window.zignagenixAPI.invoke("protocols:grants-list"); setGrants(Array.isArray(r) ? r : []); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function grant() {
    const uid = parseInt(userId, 10); if (!uid) { setErr("Enter a numeric user id"); return; }
    setBusy(true); setErr(null);
    try { const r = await window.zignagenixAPI.invoke("protocols:grant", { userId: uid, note }); if (r && r.error) setErr(r.message || r.code); else { setUserId(""); setNote(""); await load(); } }
    catch (e) { setErr(e.message); } setBusy(false);
  }
  async function revoke(uid) {
    setBusy(true); setErr(null);
    try { await window.zignagenixAPI.invoke("protocols:revoke", { userId: uid }); await load(); } catch (e) { setErr(e.message); } setBusy(false);
  }

  return (
    <Modal title="Protocol access — grants" onClose={onClose}>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ fontSize: 12, color: "#6C757D", marginBottom: 8 }}>Grant a specific user access to all protocols. Access is per-user and independent of role.</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <input style={{ ...inp, width: 120 }} placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
        <input style={{ ...inp, flex: 1 }} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        <button onClick={grant} disabled={busy} style={btnPrimary}>Grant</button>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", marginBottom: 6 }}>Active grants ({grants.length})</div>
      {grants.length === 0 ? <div style={{ fontSize: 12, color: "#9aa4b2" }}>No one has access yet.</div> : grants.map((g) => (
        <div key={g.user_id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid #f0f3f7" }}>
          <span style={{ flex: 1 }}>{g.display_name || `User #${g.user_id}`} <span style={{ color: "#9aa4b2" }}>· {g.role}</span></span>
          <span style={{ color: "#9aa4b2", fontSize: 11 }}>by {g.granted_by || "—"}</span>
          <button onClick={() => revoke(g.user_id)} disabled={busy} style={btnTinyGhost}>Revoke</button>
        </div>
      ))}
    </Modal>
  );
}

function ProtocolDocs({ protocolId, onExtracted }) {
  const [docs, setDocs] = useState([]);
  const [statuses, setStatuses] = useState({}); // docId -> extraction job
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await window.zignagenixAPI.invoke("protocols:doc:list", { protocolId });
      const list = Array.isArray(r) ? r : [];
      setDocs(list);
      const st = {};
      for (const d of list) {
        try { const s = await window.zignagenixAPI.invoke("protocols:extraction:status", { documentId: d.id }); if (s && !s.error) st[d.id] = s; } catch (_) {}
      }
      setStatuses(st);
    } catch (_) {}
  }, [protocolId]);
  useEffect(() => { load(); }, [load]);

  // Poll a freshly-uploaded doc's extraction job until it finishes.
  async function pollExtraction(docId) {
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    for (let i = 0; i < 10; i++) {
      await sleep(4000);
      let s = null;
      try { s = await window.zignagenixAPI.invoke("protocols:extraction:status", { documentId: docId }); } catch (_) {}
      if (s && !s.error) {
        setStatuses((m) => ({ ...m, [docId]: s }));
        if (s.status === "done") { setNote(`AI extracted ${s.protocols_found} protocol(s) — see the review queue above.`); onExtracted && onExtracted(); return; }
        if (s.status === "failed") { setNote(`Extraction failed: ${s.error || "unknown error"}`); return; }
      }
    }
  }

  async function onPick(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { setErr("File exceeds 25 MB."); return; }
    setBusy(true); setErr(null); setNote(null);
    try {
      const dataBase64 = await new Promise((res, rej) => {
        const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file);
      });
      const r = await window.zignagenixAPI.invoke("protocols:doc:upload", { protocolId, filename: file.name, contentType: file.type, dataBase64 });
      if (r && r.error) setErr(r.message || r.code);
      else {
        await load();
        if (r && r.extractionQueued && r.id) { setNote("Uploaded — AI is reading the document and splitting out protocols…"); pollExtraction(r.id); }
      }
    } catch (e2) { setErr(e2.message); }
    setBusy(false);
  }

  function statusBadge(docId) {
    const s = statuses[docId];
    if (!s) return null;
    if (s.status === "queued" || s.status === "running") return <span style={{ fontSize: 10, color: "#b8860b", background: "#fbf3df", padding: "1px 6px", borderRadius: 8 }}><Loader2 size={9} className="spin" /> extracting…</span>;
    if (s.status === "done") return <span style={{ fontSize: 10, color: "#1a7f5a", background: "#e6f7ee", padding: "1px 6px", borderRadius: 8 }}>✓ {s.protocols_found} protocol(s)</span>;
    if (s.status === "failed") return <span style={{ fontSize: 10, color: "#8b0000", background: "#fde8e8", padding: "1px 6px", borderRadius: 8 }} title={s.error || ""}>⚠ extract failed</span>;
    return null;
  }

  async function download(id) {
    try {
      const r = await window.zignagenixAPI.invoke("protocols:doc:get-url", { id });
      if (r && r.url) window.open(r.url, "_blank", "noopener"); else setErr((r && (r.message || r.code)) || "Download failed");
    } catch (e) { setErr(e.message); }
  }

  return (
    <div style={{ marginTop: 10, borderTop: "1px solid #f0f3f7", paddingTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase" }}>Documents ({docs.length})</span>
        <label style={{ ...uploadBtn, opacity: busy ? 0.6 : 1 }}>
          {busy ? <Loader2 size={11} className="spin" /> : <Upload size={11} />} {busy ? "Uploading…" : "Upload"}
          <input type="file" style={{ display: "none" }} disabled={busy}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.rtf,.png,.jpg,.jpeg" onChange={onPick} />
        </label>
      </div>
      {err && <div style={{ fontSize: 11, color: "#8b0000", marginBottom: 4 }}>{err}</div>}
      {note && <div style={{ fontSize: 11, color: "#256F7A", background: "#eef6f7", border: "1px solid #cfe6e9", borderRadius: 5, padding: "5px 8px", marginBottom: 6 }}>{note}</div>}
      {docs.length === 0 ? (
        <div style={{ fontSize: 11.5, color: "#9aa4b2", fontStyle: "italic" }}>No documents uploaded.</div>
      ) : docs.map((d) => (
        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 0" }}>
          <FileText size={12} color="#5a6478" />
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.original_filename}</span>
          {statusBadge(d.id)}
          <button onClick={() => download(d.id)} style={dlBtn}><Download size={11} /> Open</button>
        </div>
      ))}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 200, padding: "5vh 16px", overflowY: "auto" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 8, padding: 18, width: "100%", maxWidth: 560 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <strong style={{ fontSize: 15 }}>{title}</strong>
          <button onClick={onClose} style={{ ...btnTinyGhost, border: "none" }}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#3a4a5e", margin: "8px 0 3px" };
const inp = { width: "100%", padding: "6px 8px", fontSize: 12.5, color: "#1f2a3b", border: "1px solid #c9d6e8", borderRadius: 4, background: "#fff", boxSizing: "border-box", fontFamily: "inherit" };
const btnPrimary = { padding: "7px 13px", fontSize: 12.5, fontWeight: 600, background: "#2E8B98", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const btnSecondary = { padding: "7px 12px", fontSize: 12.5, fontWeight: 600, background: "#fff", color: "#3a4a5e", border: "1px solid #c9d6e8", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const btnTinyGhost = { padding: "3px 9px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#8b0000", border: "1px solid #f0b4b4", borderRadius: 5, cursor: "pointer" };
const btnSm = { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", fontSize: 11.5, fontWeight: 600, background: "#fff", color: "#3a4a5e", border: "1px solid #c9d6e8", borderRadius: 5, cursor: "pointer" };
const chip = { padding: "5px 11px", fontSize: 12, borderRadius: 14, background: "#fff", color: "#5a6478", border: "1px solid #c9d6e8", cursor: "pointer" };
const chipOn = { background: "#2E8B98", color: "#fff", border: "1px solid #2E8B98", fontWeight: 700 };
const errBox = { display: "flex", alignItems: "center", gap: 6, background: "#fde8e8", border: "1px solid #f5a5a5", padding: 9, borderRadius: 4, marginBottom: 10, color: "#8b0000", fontSize: 12.5 };
const encBox = { display: "flex", alignItems: "center", gap: 6, background: "#f4f9f7", border: "1px solid #bfe0d2", padding: 11, borderRadius: 6, marginBottom: 12, color: "#1f2a3b", fontSize: 12.5 };
const uploadBtn = { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 600, color: "#2E8B98", background: "#e7f3f5", border: "1px solid #b6dde2", borderRadius: 6, padding: "3px 9px", cursor: "pointer" };
const dlBtn = { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: "#2E8B98", background: "#fff", border: "1px solid #c9d6e8", borderRadius: 5, padding: "2px 8px", cursor: "pointer", flexShrink: 0 };
